import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/useAuthStore';
import { resolvePostLoginRoute } from '../../utils/postLoginRoute';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { Card, CardContent } from '@/components/ui/card';
import iconVib3 from '@/assets/icon-vib3.svg';

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" />
      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z" />
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58Z" />
    </svg>
  );
}

export default function LeadSignupPage() {
  const navigate = useNavigate();
  const { signIn, signUpAsLead, signInWithGoogle, requestPasswordReset, restoreSession, loading, error, token, user, userRole } = useAuthStore();
  const [mode, setMode] = useState<'signin' | 'signup' | 'forgot'>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  // PP/TC agreement — required before sign-up only (sign-in and forgot don't gate;
  // consent is captured at account creation, not on every subsequent login).

  useEffect(() => {
    restoreSession();
  }, [restoreSession]);

  // Already authenticated → skip the form and jump directly to whichever
  // step the user left off on (sign / connect / confirm / processing /
  // generate-report / report).
  useEffect(() => {
    if (!token || !user?.id) return;
    let cancelled = false;
    (async () => {
      const route = await resolvePostLoginRoute(user.id, {
        isEmployee: userRole === 'employee',
      });
      if (!cancelled) navigate(route, { replace: true });
    })();
    return () => { cancelled = true; };
  }, [token, user?.id, userRole, navigate]);

  const callbackUrl = `${window.location.origin}/auth/callback`;
  const isSignUp = mode === 'signup';
  const isForgot = mode === 'forgot';

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isForgot) {
      await requestPasswordReset(email);
      // Anti-enumeration: show success regardless of whether the email
      // exists. We only stay on the form if requestPasswordReset surfaced
      // a real failure (network / 5xx) via state.error.
      const state = useAuthStore.getState();
      if (state.error) return;
      setResetSent(true);
      return;
    }
    if (isSignUp) {
      await signUpAsLead(email, password);
    } else {
      await signIn(email, password);
    }
    const state = useAuthStore.getState();
    if (state.error) return;
    if (state.token && state.user?.id) {
      // Sign-in path: route by current workflow stage. Sign-up users have no
      // signed agreement yet → /sign anyway, so this is the same call.
      const route = await resolvePostLoginRoute(state.user.id, {
        isEmployee: state.userRole === 'employee',
      });
      navigate(route, { replace: true });
    } else if (isSignUp) {
      // No session after signup → Supabase requires email confirmation.
      setSubmitted(true);
    }
  };

  const switchMode = (next: 'signin' | 'signup' | 'forgot') => {
    setMode(next);
    setResetSent(false);
    // Drop password when leaving sign-in/up, so the field doesn't briefly
    // flash with a stale value when the user toggles back.
    if (next === 'forgot') setPassword('');
  };

  const handleGoogle = () => {
    signInWithGoogle(callbackUrl);
  };

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Card className="w-full max-w-[400px]">
          <CardContent className="p-8 text-center">
            <img src={iconVib3} alt="Vib3" className="mx-auto mb-3 h-8 w-auto" />
            <h2 className="text-lg font-semibold text-foreground">Check your email</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              We sent a confirmation link to <strong>{email}</strong>. Click it to activate your account.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const title = isForgot
    ? 'Reset your password'
    : isSignUp
      ? 'Create your account'
      : 'Welcome back';
  const subtitle = isForgot
    ? "Enter your email and we'll send you a reset link."
    : isSignUp
      ? 'Join the lead portal'
      : 'Sign in to continue';
  const submitLabel = loading
    ? 'Loading...'
    : isForgot
      ? 'Send reset link'
      : isSignUp
        ? 'Create Account'
        : 'Sign In';

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Card className="w-full max-w-[400px]">
        <CardContent className="p-8">
          <div className="mb-6 text-center">
            <img src={iconVib3} alt="Vib3" className="mx-auto mb-3 h-8 w-auto" />
            <h2 className="text-lg font-semibold text-foreground">{title}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
          </div>

          {!isForgot && (
            <>
              <Button
                type="button"
                variant="outline"
                className="mb-4 w-full cursor-pointer gap-2"
                onClick={handleGoogle}
                disabled={loading}
              >
                <GoogleIcon />
                Continue with Google
              </Button>

              <div className="relative mb-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">or</span>
                </div>
              </div>
            </>
          )}

          {isForgot && resetSent ? (
            <div className="space-y-4 text-center">
              <p className="text-sm text-muted-foreground">
                If <strong className="text-foreground">{email}</strong> is in our
                system, we just sent a reset link. Check your inbox (and spam).
              </p>
              <Button
                type="button"
                variant="outline"
                className="w-full cursor-pointer"
                onClick={() => switchMode('signin')}
              >
                Back to sign in
              </Button>
            </div>
          ) : (
            <form onSubmit={handleEmailSubmit} className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>
              {!isForgot && (
                <div className="space-y-1">
                  <Label htmlFor="password">Password</Label>
                  <PasswordInput
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={isSignUp ? 'Min 6 characters' : 'Your password'}
                    required
                    minLength={6}
                    autoComplete={isSignUp ? 'new-password' : 'current-password'}
                  />
                </div>
              )}

              {error && <p className="text-sm text-destructive">{error}</p>}

              <Button type="submit" disabled={loading} className="w-full cursor-pointer">
                {submitLabel}
              </Button>
            </form>
          )}

          <p className="mt-4 text-center text-sm text-muted-foreground">
            {isForgot ? (
              <>
                Remembered it?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('signin')}
                  className="cursor-pointer border-none bg-transparent p-0 text-sm font-medium text-foreground hover:underline"
                >
                  Back to sign in
                </button>
              </>
            ) : isSignUp ? (
              <>
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('signin')}
                  className="cursor-pointer border-none bg-transparent p-0 text-sm font-medium text-foreground hover:underline"
                >
                  Sign In
                </button>
              </>
            ) : (
              <>
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('signup')}
                  className="cursor-pointer border-none bg-transparent p-0 text-sm font-medium text-foreground hover:underline"
                >
                  Sign Up
                </button>
              </>
            )}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
